/**
 * URL Router — Pure URL Functions
 *
 * Two pure functions for bidirectional URL ↔ view state mapping.
 * No React, no Supabase, no side effects — same pattern as cpmEngine.js.
 *
 * View state shape: { view: string, projectId?: string, tab?: string, filter?: string, editTemplateId?: string }
 */

// Route definitions: [pattern, view, paramExtractor]
// Ordered most-specific first to avoid ambiguous matches.
const ROUTES = [
  [/^\/projects\/new\/?$/, 'new-project'],
  [/^\/projects\/([^/]+)\/gantt\/?$/, 'gantt', m => ({ projectId: m[1] })],
  [/^\/projects\/([^/]+)\/budget\/?$/, 'budget-detail', m => ({ projectId: m[1] })],
  [/^\/projects\/([^/]+)\/report\/?$/, 'project-report', m => ({ projectId: m[1] })],
  [/^\/projects\/([^/]+)\/documentation\/?$/, 'project-documentation', m => ({ projectId: m[1] })],
  [/^\/projects\/([^/]+)\/?$/, 'project', m => ({ projectId: m[1] })],
  [/^\/reports\/portfolio\/?$/, 'portfolio-report'],
  [/^\/reports\/vendor-insurance\/?$/, 'vendor-insurance'],
  [/^\/reports\/overdue-blocked\/?$/, 'overdue-blocked'],
  [/^\/reports\/change-orders\/?$/, 'change-orders-report'],
  [/^\/reports\/budget-risk\/?$/, 'budget-risk'],
  [/^\/reports\/?$/, 'reports'],
  [/^\/contacts\/?$/, 'contacts'],
  [/^\/templates\/builder\/?$/, 'template-builder'],
  [/^\/settings\/?$/, 'settings'],
  [/^\/help\/?$/, 'help'],
  [/^\/?$/, 'dashboard'],
]

/**
 * Parse a URL pathname + search string into view state.
 *
 * @param {string} pathname - e.g. "/projects/abc-123"
 * @param {string} [search=""] - e.g. "?tab=tasks&filter=Blocked"
 * @returns {{ view: string, projectId?: string, tab?: string, filter?: string, editTemplateId?: string }}
 */
export function pathToViewState(pathname, search = '') {
  const path = (pathname || '/').replace(/\/+$/, '') || '/'

  for (const [pattern, view, extractParams] of ROUTES) {
    const match = path.match(pattern)
    if (match) {
      const params = extractParams ? extractParams(match) : {}
      const searchParams = new URLSearchParams(search)

      const tab = searchParams.get('tab')
      const filter = searchParams.get('filter')
      const editTemplateId = searchParams.get('edit')

      return {
        view,
        ...params,
        ...(tab ? { tab } : {}),
        ...(filter ? { filter } : {}),
        ...(editTemplateId ? { editTemplateId } : {}),
      }
    }
  }

  // Unknown path → dashboard
  return { view: 'dashboard' }
}

// Inverse mapping: view → path template
const VIEW_TO_PATH = {
  'dashboard': () => '/',
  'new-project': () => '/projects/new',
  'project': p => `/projects/${p.projectId}`,
  'gantt': p => `/projects/${p.projectId}/gantt`,
  'budget-detail': p => `/projects/${p.projectId}/budget`,
  'project-report': p => `/projects/${p.projectId}/report`,
  'project-documentation': p => `/projects/${p.projectId}/documentation`,
  'reports': () => '/reports',
  'portfolio-report': () => '/reports/portfolio',
  'vendor-insurance': () => '/reports/vendor-insurance',
  'overdue-blocked': () => '/reports/overdue-blocked',
  'change-orders-report': () => '/reports/change-orders',
  'budget-risk': () => '/reports/budget-risk',
  'contacts': () => '/contacts',
  'template-builder': () => '/templates/builder',
  'settings': () => '/settings',
  'help': () => '/help',
}

/**
 * Generate a URL path from view state.
 *
 * @param {string} view - e.g. "project"
 * @param {{ projectId?: string, tab?: string, filter?: string, editTemplateId?: string }} [params={}]
 * @returns {string} URL path with query string, e.g. "/projects/abc-123?tab=tasks"
 */
export function viewStateToPath(view, params = {}) {
  const pathFn = VIEW_TO_PATH[view]
  if (!pathFn) return '/'

  const basePath = pathFn(params)

  // Build query string from known params
  const searchParams = new URLSearchParams()
  if (params.tab) searchParams.set('tab', params.tab)
  if (params.filter) searchParams.set('filter', params.filter)
  if (params.editTemplateId) searchParams.set('edit', params.editTemplateId)

  const qs = searchParams.toString()
  return qs ? `${basePath}?${qs}` : basePath
}
